import { toPng } from 'html-to-image';
import { useEffect, useState, useRef } from 'react';

// Define the style options
const cardStyles = [
  {
    id: 'gradient-dark',
    name: 'Gradient Purple/Blue',
    cardClass: 'bg-gradient-to-r from-purple-400 to-blue-400',
    textClass: 'text-white'
  },
  {
    id: 'gradient-light',
    name: 'Light Gradient',
    cardClass: 'bg-gradient-to-r from-purple-100 to-blue-100',
    textClass: 'text-gray-700'
  },
  {
    id: 'solid-dark',
    name: 'Solid Black',
    cardClass: 'bg-[#212326]',
    textClass: 'text-white'
  },
  {
    id: 'solid-light',
    name: 'Beige',
    cardClass: 'bg-amber-50',
    textClass: 'text-gray-800'
  }
];

// Troll mode styles
const trollStyles = [
  ...cardStyles,
];

// Meme mode styles - combining both sets
const memeStyles = [
  ...cardStyles
];

export function VisualCard({ 
  profileImage, 
  summary, 
  username, 
  isTrollMode = false, 
  isMemeMode = false, 
  memeImage = null,
  currentCardMode // Add this prop to track the actual mode of the current card content
}) {
  const [localProfileImage, setLocalProfileImage] = useState('');
  const [profileImageError, setProfileImageError] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(cardStyles[0]);
  const cardRef = useRef(null);
  const usernameRef = useRef(null);
  
  // Reset selected style only when the card content actually changes (on generate)
  useEffect(() => {
    // Determine which style set to use based on the CURRENT CARD CONTENT mode
    // (not the selected UI mode)
    if (currentCardMode === 'meme') {
      setSelectedStyle(memeStyles[0]);
    } else if (currentCardMode === 'troll') {
      setSelectedStyle(trollStyles[0]);
    } else {
      setSelectedStyle(cardStyles[0]);
    }
  }, [currentCardMode]); // Only change when the card content mode changes
  
  // Enhanced image loading with multiple fallback strategies
  useEffect(() => {
    if (!profileImage && !isMemeMode) {
      setLocalProfileImage('https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png');
      return;
    }
    
    // Skip profile image loading for meme mode
    if (currentCardMode === 'meme') return;
    
    // Function to convert image to data URL
    const loadImageAsDataUrl = (imgUrl) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          
          const dataUrl = canvas.toDataURL('image/png');
          setLocalProfileImage(dataUrl);
          setProfileImageError(false);
        } catch (e) {
          console.error("Error converting image:", e);
          handleImageError();
        }
      };
      
      img.onerror = () => {
        console.warn(`Failed to load image from URL: ${imgUrl}`);
        handleImageError();
      };
      
      img.src = imgUrl;
    };
    
    // Handle image loading errors
    const handleImageError = () => {
      // Try loading through proxy if not already using it
      if (!profileImage.includes('/api/proxy-image') && !profileImageError) {
        const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 
          'http://localhost:4000' : 
          `${window.location.protocol}//${window.location.host}`);
          
        const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(profileImage)}`;
        console.log("Trying proxy URL:", proxyUrl);
        loadImageAsDataUrl(proxyUrl);
        setProfileImageError(true); // Mark that we've tried the proxy
      } else {
        // If proxy also fails, use default image
        console.log("Using default profile image");
        setLocalProfileImage('https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png');
      }
    };
    
    // Start loading the image
    loadImageAsDataUrl(profileImage);
  }, [profileImage, profileImageError, currentCardMode]); // Changed dependency to currentCardMode

  // Function to handle style selection
  const handleStyleSelect = (style) => {
    setSelectedStyle(style);
  };

  // Get appropriate styles based on current card mode (not UI mode)
  const getStyles = () => {
    if (currentCardMode === 'meme') return memeStyles;
    if (currentCardMode === 'troll') return trollStyles;
    return cardStyles;
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <div 
        id={currentCardMode === 'meme' ? "meme-card" : "card"} 
        ref={cardRef}
        className={`w-[600px] min-w-[600px] rounded-xl shadow-lg ${selectedStyle.cardClass} ${selectedStyle.textClass} overflow-hidden ${currentCardMode === 'meme' ? 'p-0' : 'p-6'}`}
      >
        {currentCardMode === 'meme' ? (
          // Meme mode layout with no padding on the container
          <div className="flex">
            {/* Left side - Image container with fixed width and full height */}
            <div className="w-1/2 relative bg-slate-800" style={{ minHeight: '240px' }}>
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  src={memeImage} 
                  alt="Meme" 
                  className="max-w-full max-h-full h-full object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://media.giphy.com/media/xT0GqgeTVaAdWZD1uw/giphy.gif";
                  }}
                />
              </div>
            </div>
            
            {/* Right side - Text content and footer with its own padding */}
            <div className="w-1/2 flex flex-col justify-between p-6">
              {/* Text content */}
              <div className="pb-2">
                <p className="text-lg font-bold leading-tight">
                  {summary}
                </p>
              </div>
              
              {/* Footer positioned at bottom right */}
              <div className={`text-right text-sm ${selectedStyle.textClass} mt-auto`}>
                <span className='opacity-80'>Made by</span>&nbsp;
                <span className='font-extrabold'>x-summary.app</span>
                <span className="ml-1">😂</span>
              </div>
            </div>
          </div>
        ) : (
          // Normal/Troll mode layout (with padding)
          <>
            <div className="flex items-center mb-4">
              <img 
                src={localProfileImage || 'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png'} 
                alt="Profile" 
                className="w-16 h-16 rounded-full border-2 border-white shadow mr-3"
                style={{ objectFit: 'cover', flexShrink: 0 }}
              />
              <span ref={usernameRef} className="text-xl font-semibold">
                @{username}
                {currentCardMode === 'troll' && <span className="ml-2">🤪</span>}
              </span>
            </div>

            <ul className="mt-4 text-lg">
              {summary.split('\n').map((bullet, i) => (
                <li key={i} className="mb-2 font-semibold">{bullet}</li>
              ))}
            </ul>
            
            <div className={`mt-6 text-center text-sm ${selectedStyle.textClass}`}>
              <span className='opacity-80'>Made by</span>&nbsp;
              <span className='font-extrabold'>x-summary.app</span>
              {currentCardMode === 'troll' && <span className="ml-1">👹</span>}
            </div>
          </>
        )}
      </div>
      
      {/* Style selector circles */}
      <div className="flex justify-center gap-4 my-4">
        {getStyles().map(style => (
          <div 
            key={style.id} 
            onClick={() => handleStyleSelect(style)}
            className={`
              w-12 h-12 rounded-full cursor-pointer flex items-center justify-center
              transition-all duration-300 hover:scale-110
              ${style.cardClass} border-2 
              ${selectedStyle.id === style.id ? 'border-blue-500 scale-110' : 'border-transparent'}
            `}
            title={style.name}
          >
            {selectedStyle.id === style.id && (
              <div className="w-3 h-3 bg-white rounded-full"></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export async function copyImageToClipboard() {
  const cardElement = document.getElementById('card') || document.getElementById('meme-card');
  if (!cardElement) {
    alert('Card element not found.');
    return;
  }
  
  try {
    // Use html-to-image instead of html2canvas
    const dataUrl = await toPng(cardElement, {
      quality: 0.95,
      pixelRatio: 2,
      backgroundColor: null,
      // These options help ensure everything is properly rendered
      cacheBust: true,
      skipFonts: false,
      width: cardElement.offsetWidth,
      height: cardElement.offsetHeight
    });
    
    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      alert('✅ Image copied! Now paste it into X.');
    } catch (error) {
      console.error("Clipboard error:", error);
      alert('Clipboard copy not supported on your device. Please download the image instead.');
    }
  } catch (error) {
    console.error("Image generation error:", error);
    alert('Error creating image. Try downloading instead.');
  }
}

export async function downloadCard() {
  const cardElement = document.getElementById('card') || document.getElementById('meme-card');
  if (!cardElement) {
    alert('Card element not found.');
    return;
  }
  
  try {
    // Use html-to-image instead of html2canvas
    const dataUrl = await toPng(cardElement, {
      quality: 0.95,
      pixelRatio: 2,
      backgroundColor: null,
      // These options help ensure everything is properly rendered
      cacheBust: true,
      skipFonts: false,
      width: cardElement.offsetWidth,
      height: cardElement.offsetHeight
    });
    
    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    
    downloadOrShare(blob);
  } catch (error) {
    console.error("Image generation error:", error);
    alert('Error creating image. Please try again.');
  }
}

// Helper function for downloading or sharing the blob
function downloadOrShare(blob) {
  // Check if Web Share API is available (primarily for mobile devices)
  if (navigator.share && navigator.canShare) {
    // Try to use the Web Share API with file sharing
    const file = new File([blob], 'summary-card.png', { type: 'image/png' });
    
    if (navigator.canShare({ files: [file] })) {
      try {
        navigator.share({
          title: 'X Post Summary',
          text: 'Check out this X post summary',
          files: [file]
        }).catch(error => {
          console.error("Share API error:", error);
          // Continue to fallback if share API fails
          downloadFallback(blob);
        });
        return; // Exit function if sharing was successful
      } catch (error) {
        console.error("Share API error:", error);
        // Continue to fallback if share API fails
      }
    }
  }
  
  downloadFallback(blob);
}

// Fallback download function
function downloadFallback(blob) {
  // Fallback to traditional download for desktop or if Web Share API is not available
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'summary-card.png';
  link.click();
  
  // Clean up object URL to avoid memory leaks
  setTimeout(() => URL.revokeObjectURL(url), 100);
}